import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type AdminApiTokenRecord, type CommunityGroupRecord, type CommunityPartnerOrganizationRecord, type TeamMemberRecord, type CredentialResetPurpose, type MeResponse } from '../utils/api'
import { useT } from '../i18n'
import SectionNav from '../components/SectionNav'
import Drawer from '../components/Drawer'
import {
  getOwnerConsoleSessionIdentityLabelKey,
  getOwnerConsoleSessionIdentityValue
} from './ownerConsoleSessionIdentity'
import {
  getOwnerConsoleCredentialActionKey,
  getOwnerConsoleMemberStatus,
  getOwnerConsoleCredentialStatusKey
} from './ownerConsoleCredentialStatus'
import { getMemberRoleDraftState } from './ownerConsoleMemberRoleDraft'

type LoadState = {
  isLoading: boolean
  error: string
}

type MemberFilter = 'all' | 'active' | 'revoked'
type OwnerConsoleMainSection = 'members' | 'tokens'
type OwnerConsoleDrawerSection = 'organizations' | 'groups' | null

const getMemberStatusRank = (member: TeamMemberRecord): number => member.status === 'active' ? 0 : 1

export default function OwnerConsole() {
  const t = useT()
  const navigate = useNavigate()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [members, setMembers] = useState<TeamMemberRecord[]>([])
  const [groups, setGroups] = useState<CommunityGroupRecord[]>([])
  const [partnerOrganizations, setPartnerOrganizations] = useState<CommunityPartnerOrganizationRecord[]>([])
  const [tokens, setTokens] = useState<AdminApiTokenRecord[]>([])
  const [loadState, setLoadState] = useState<LoadState>({ isLoading: true, error: '' })
  const [memberRoleDrafts, setMemberRoleDrafts] = useState<Record<string, 'viewer' | 'contributor' | 'editor'>>({})
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all')
  const [memberSearch, setMemberSearch] = useState('')
  const [issuingPasswordResetMemberId, setIssuingPasswordResetMemberId] = useState<string | null>(null)
  const [issuedPasswordReset, setIssuedPasswordReset] = useState<null | {
    memberId: string
    memberLabel: string
    purpose: CredentialResetPurpose
    resetUrl: string
    expiresAt: string
  }>(null)
  // Crew Mode Charter D-4 shared identity mechanism: per-member PIN drafts
  // and in-flight state, mirroring the role-draft / password-reset state
  // shapes already established on this screen.
  const [operatorPinDrafts, setOperatorPinDrafts] = useState<Record<string, string>>({})
  const [settingPinMemberId, setSettingPinMemberId] = useState<string | null>(null)
  const [clearingPinMemberId, setClearingPinMemberId] = useState<string | null>(null)
  const [pinStatusMessage, setPinStatusMessage] = useState<string | null>(null)
  const [partnerOrganizationName, setPartnerOrganizationName] = useState('')
  const [groupName, setGroupName] = useState('')
  const [groupMemberDrafts, setGroupMemberDrafts] = useState<Record<string, string>>({})
  const [groupPartnerOrganizationDrafts, setGroupPartnerOrganizationDrafts] = useState<Record<string, string>>({})
  const [groupRoleDrafts, setGroupRoleDrafts] = useState<Record<string, 'owner' | 'editor' | 'viewer'>>({})

  const [label, setLabel] = useState('')
  const [role, setRole] = useState<'viewer' | 'contributor' | 'editor'>('editor')
  const [createdToken, setCreatedToken] = useState<string>('')

  const [activeSection, setActiveSection] = useState<OwnerConsoleMainSection>('members')
  const [drawerSection, setDrawerSection] = useState<OwnerConsoleDrawerSection>(null)

  const reload = async () => {
    setLoadState({ isLoading: true, error: '' })
    try {
      const [resolvedMe, resolvedMembers, resolvedGroups, resolvedPartnerOrganizations, list] = await Promise.all([
        api.me.get(),
        api.teamMembers.list({ includeRevoked: true }),
        api.communityGroups.list(),
        api.communityPartnerOrganizations.list(),
        api.adminApiTokens.list()
      ])
      setMe(resolvedMe)
      setMembers(resolvedMembers)
      setGroups(resolvedGroups)
      setPartnerOrganizations(resolvedPartnerOrganizations)
      setMemberRoleDrafts(
        Object.fromEntries(
          resolvedMembers
            .filter((member) => member.role !== 'admin')
            .map((member) => [member.id, member.role])
        ) as Record<string, 'viewer' | 'contributor' | 'editor'>
      )
      setTokens(list)
      setLoadState({ isLoading: false, error: '' })
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.failedToLoad') })
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canManage = useMemo(() => me?.role === 'admin', [me?.role])
  const memberCounts = useMemo(() => ({
    all: members.length,
    active: members.filter((member) => member.status === 'active').length,
    revoked: members.filter((member) => member.status === 'revoked').length
  }), [members])
  const normalizedMemberSearch = memberSearch.trim().toLowerCase()
  const filteredMembers = useMemo(() => members.filter((member) => {
    const matchesFilter = memberFilter === 'active'
      ? member.status === 'active'
      : memberFilter === 'revoked'
        ? member.status === 'revoked'
        : true

    if (!matchesFilter) return false
    if (!normalizedMemberSearch) return true

    const haystack = [
      member.label,
      member.actorId,
      member.userId ?? '',
      member.tenantMembershipId ?? member.id,
      member.status,
      member.credentialStatus
    ].join(' ').toLowerCase()

    return haystack.includes(normalizedMemberSearch)
  }), [members, memberFilter, normalizedMemberSearch])
  const sortedMembers = useMemo(() => [...filteredMembers].sort((left, right) => {
    const statusDiff = getMemberStatusRank(left) - getMemberStatusRank(right)
    if (statusDiff !== 0) return statusDiff

    const labelDiff = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
    if (labelDiff !== 0) return labelDiff

    return left.id.localeCompare(right.id)
  }), [filteredMembers])
  const activeMembers = useMemo(() => members.filter((member) => member.status === 'active'), [members])

  const createUserToken = async () => {
    setCreatedToken('')
    const trimmed = label.trim()
    if (!trimmed) {
      setLoadState((prev) => ({ ...prev, error: t('ownerConsole.error.enterLabel') }))
      return
    }

    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      const result = await api.adminApiTokens.create({ label: trimmed, role })
      setCreatedToken(result.token)
      setLabel('')
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.createFailed') })
    }
  }

  const revoke = async (id: string) => {
    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.adminApiTokens.revoke(id)
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.revokeFailed') })
    }
  }

  const saveMemberRole = async (member: TeamMemberRecord) => {
    if (member.role === 'admin') return
    const nextRole = memberRoleDrafts[member.id] ?? member.role
    if (nextRole === member.role) return

    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.teamMembers.update(member.id, { role: nextRole })
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.memberUpdateFailed') })
    }
  }

  const revokeMember = async (memberId: string) => {
    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.teamMembers.revoke(memberId)
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.memberRevokeFailed') })
    }
  }

  const issueMemberPasswordReset = async (member: TeamMemberRecord) => {
    setIssuingPasswordResetMemberId(member.id)
    setLoadState((prev) => ({ ...prev, error: '' }))
    try {
      const result = await api.teamMembers.issuePasswordReset(member.id)
      const resetUrl = `${globalThis.location.origin}/reset-password?token=${encodeURIComponent(result.token)}`
      setIssuedPasswordReset({
        memberId: member.id,
        memberLabel: member.label,
        purpose: result.purpose,
        resetUrl,
        expiresAt: result.expiresAt
      })
    } catch (e) {
      setLoadState((prev) => ({ ...prev, error: e instanceof Error ? e.message : t('ownerConsole.error.passwordResetFailed') }))
    } finally {
      setIssuingPasswordResetMemberId(null)
    }
  }

  const setOperatorPin = async (member: TeamMemberRecord) => {
    const pin = (operatorPinDrafts[member.id] ?? '').trim()
    if (!/^\d{4}$/.test(pin)) {
      setLoadState((prev) => ({ ...prev, error: t('ownerConsole.pin.invalid') }))
      return
    }

    setSettingPinMemberId(member.id)
    setLoadState((prev) => ({ ...prev, error: '' }))
    setPinStatusMessage(null)
    try {
      await api.teamMembers.setPin(member.id, pin)
      setOperatorPinDrafts((prev) => {
        const next = { ...prev }
        delete next[member.id]
        return next
      })
      setPinStatusMessage(t('ownerConsole.pin.setSuccess', { label: member.label }))
    } catch (e) {
      setLoadState((prev) => ({ ...prev, error: e instanceof Error ? e.message : t('ownerConsole.error.pinSetFailed') }))
    } finally {
      setSettingPinMemberId(null)
    }
  }

  const clearOperatorPin = async (member: TeamMemberRecord) => {
    setClearingPinMemberId(member.id)
    setLoadState((prev) => ({ ...prev, error: '' }))
    setPinStatusMessage(null)
    try {
      await api.teamMembers.clearPin(member.id)
      setPinStatusMessage(t('ownerConsole.pin.clearSuccess', { label: member.label }))
    } catch (e) {
      setLoadState((prev) => ({ ...prev, error: e instanceof Error ? e.message : t('ownerConsole.error.pinClearFailed') }))
    } finally {
      setClearingPinMemberId(null)
    }
  }

  const createGroup = async () => {
    const trimmed = groupName.trim()
    if (!trimmed) {
      setLoadState((prev) => ({ ...prev, error: t('ownerConsole.error.enterGroupName') }))
      return
    }

    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.communityGroups.create({ displayName: trimmed })
      setGroupName('')
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.groupCreateFailed') })
    }
  }

  const createPartnerOrganization = async () => {
    const trimmed = partnerOrganizationName.trim()
    if (!trimmed) {
      setLoadState((prev) => ({ ...prev, error: t('ownerConsole.error.enterPartnerOrganizationName') }))
      return
    }

    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.communityPartnerOrganizations.create({ displayName: trimmed })
      setPartnerOrganizationName('')
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.partnerOrganizationCreateFailed') })
    }
  }

  const addGroupMember = async (group: CommunityGroupRecord) => {
    const tenantMembershipId = (groupMemberDrafts[group.id] ?? '').trim()
    if (!tenantMembershipId) {
      setLoadState((prev) => ({ ...prev, error: t('ownerConsole.error.enterGroupMember') }))
      return
    }

    const nextRole = groupRoleDrafts[group.id] ?? 'viewer'
    const partnerOrganizationId = (groupPartnerOrganizationDrafts[group.id] ?? '').trim() || undefined

    setLoadState((prev) => ({ ...prev, isLoading: true, error: '' }))
    try {
      await api.communityGroups.addMembership(group.id, {
        tenantMembershipId,
        role: nextRole,
        partnerOrganizationId
      })
      setGroupMemberDrafts((prev) => ({
        ...prev,
        [group.id]: ''
      }))
      setGroupPartnerOrganizationDrafts((prev) => ({
        ...prev,
        [group.id]: ''
      }))
      setGroupRoleDrafts((prev) => ({
        ...prev,
        [group.id]: 'viewer'
      }))
      await reload()
    } catch (e) {
      setLoadState({ isLoading: false, error: e instanceof Error ? e.message : t('ownerConsole.error.groupMembershipCreateFailed') })
    }
  }

  const currentSessionIdentityLabel = t(getOwnerConsoleSessionIdentityLabelKey(me))
  const currentSessionIdentityValue = getOwnerConsoleSessionIdentityValue(me)

  return (
    <div style={{ maxWidth: 980 }}>
      <h2 style={{ marginTop: 0 }}>{t('ownerConsole.title')}</h2>
      <p style={{ color: '#6b7280', marginTop: 8 }}>
        {t('ownerConsole.subtitle')}
      </p>

      {me && (
        <div style={{ padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', marginTop: 12 }}>
          <div><b>{t('ownerConsole.me.tenant')}</b> {me.tenantKey}</div>
          <div><b>{t('ownerConsole.me.role')}</b> {me.role}</div>
          <div><b>{t('ownerConsole.me.accountName')}</b> {me.accountDisplayName ?? '—'}</div>
          <div><b>{t('ownerConsole.me.username')}</b> {me.username ?? '—'}</div>
          <div><b>{t('ownerConsole.me.email')}</b> {me.email ?? '—'}</div>
          <details style={{ marginTop: 6 }}>
            <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>{t('createMember.technicalDetails')}</summary>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              <div><b>{t('ownerConsole.me.membership')}</b> {me.tenantMembershipId ?? '—'}</div>
              <div><b>{t('ownerConsole.me.user')}</b> {me.userId ?? '—'}</div>
              <div><b>{t('ownerConsole.me.actor')}</b> {me.actorId ?? '—'}</div>
              <div><b>{currentSessionIdentityLabel}</b> {currentSessionIdentityValue}</div>
            </div>
          </details>
        </div>
      )}

      {loadState.error && (
        <div style={{ padding: 12, borderRadius: 8, marginTop: 12, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
          {loadState.error}
        </div>
      )}

      {!canManage && me && (
        <div style={{ padding: 12, borderRadius: 8, marginTop: 12, backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
          {t('ownerConsole.notice.notAdmin')}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
      <SectionNav
        items={[
          { key: 'members', label: t('ownerConsole.nav.members'), isActive: activeSection === 'members', onSelect: () => setActiveSection('members') },
          // Findings #139: no "Tokens" tab — those are technical access keys
          // from the shared-token days, not how anyone signs in (password /
          // PIN); members are revoked under Miembros. The section below stays
          // for a later admin-only view.
          { key: 'organizations', label: t('ownerConsole.nav.organizations'), isActive: drawerSection === 'organizations', onSelect: () => setDrawerSection('organizations') },
          { key: 'groups', label: t('ownerConsole.nav.groups'), isActive: drawerSection === 'groups', onSelect: () => setDrawerSection('groups') }
        ]}
      >
      {activeSection === 'members' && (
      <div style={{ padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>{t('ownerConsole.members.title')}</h3>
          {canManage ? (
            <button
              type="button"
              data-testid="owner-console-add-member"
              onClick={() => navigate('/create-account')}
              style={{
                padding: '10px 16px',
                borderRadius: 999,
                border: '1px solid #111827',
                backgroundColor: '#111827',
                color: 'white',
                fontWeight: 800,
                cursor: 'pointer'
              }}
            >
              {t('ownerConsole.members.addMember')}
            </button>
          ) : null}
        </div>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('ownerConsole.members.subtitle')}</p>
        <label style={{ display: 'grid', gap: 6, marginTop: 12, marginBottom: 12 }}>
          <span style={{ fontWeight: 600 }}>{t('ownerConsole.members.searchLabel')}</span>
          <input
            data-testid="member-search-input"
            value={memberSearch}
            onChange={(event) => setMemberSearch(event.target.value)}
            placeholder={t('ownerConsole.members.searchPlaceholder')}
            style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
          />
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, marginBottom: 12 }}>
          {([
            ['all', memberCounts.all],
            ['active', memberCounts.active],
            ['revoked', memberCounts.revoked]
          ] as const).map(([value, count]) => {
            const selected = memberFilter === value
            return (
              <button
                key={value}
                type="button"
                data-testid={`member-filter-${value}`}
                onClick={() => setMemberFilter(value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: selected ? '1px solid #111827' : '1px solid #d1d5db',
                  backgroundColor: selected ? '#111827' : 'white',
                  color: selected ? 'white' : '#111827',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                {t(`ownerConsole.members.filter.${value}` as const)} ({count})
              </button>
            )
          })}
        </div>
        {loadState.isLoading ? (
          <div style={{ color: '#6b7280' }}>{t('ownerConsole.members.loading')}</div>
        ) : members.length === 0 ? (
          <div style={{ color: '#6b7280' }}>{t('ownerConsole.members.empty')}</div>
        ) : sortedMembers.length === 0 ? (
          <div style={{ color: '#6b7280' }}>{t('ownerConsole.members.emptyFiltered')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.member')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.role')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.status')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.lastUsed')}</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>{t('ownerConsole.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((member) => {
                  const isCurrentMembership = me?.tenantMembershipId === member.id
                  const isRevokedMember = member.status === 'revoked'
                  const editable = member.role !== 'admin' && !isCurrentMembership && !isRevokedMember
                  const canIssuePasswordReset = canManage && !isRevokedMember && !loadState.isLoading && issuingPasswordResetMemberId !== member.id
                  const draftRole = memberRoleDrafts[member.id] ?? (member.role === 'admin' ? 'editor' : member.role)
                  // v2.1 finding #30: surface the "unsaved role change" state
                  // both inline (chip next to dropdown) and on the Save button
                  // (primary style when pending, ghost when clean) so the
                  // operator cannot believe the change persisted on selection.
                  const roleDraftState = getMemberRoleDraftState({ currentRole: member.role, draftRole: memberRoleDrafts[member.id] })
                  const hasPendingRoleDraft = roleDraftState === 'pending'
                  const saveButtonDisabled = !editable || !canManage || loadState.isLoading || !hasPendingRoleDraft

                  return (
                    <tr key={member.id} data-testid={`member-row-${member.id}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 8px' }}>
                        {member.label}
                        <details>
                          <summary style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>{t('createMember.technicalDetails')}</summary>
                          <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>
                            <div>{t('ownerConsole.table.membership')}: {member.tenantMembershipId ?? member.id}</div>
                            <div>{t('ownerConsole.table.user')}: {member.userId ?? '—'}</div>
                            <div>{t('ownerConsole.table.actor')}: {member.actorId}</div>
                          </div>
                        </details>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        {editable ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <select
                              data-testid={`member-role-${member.id}`}
                              value={draftRole}
                              onChange={(event) => setMemberRoleDrafts((prev) => ({
                                ...prev,
                                [member.id]: event.target.value as 'viewer' | 'contributor' | 'editor'
                              }))}
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: hasPendingRoleDraft ? '2px solid #f59e0b' : '1px solid #d1d5db',
                                backgroundColor: hasPendingRoleDraft ? '#fffbeb' : 'white'
                              }}
                              disabled={!canManage || loadState.isLoading}
                            >
                              <option value="viewer">viewer</option>
                              <option value="contributor">contributor</option>
                              <option value="editor">editor</option>
                            </select>
                            {hasPendingRoleDraft && (
                              <span
                                data-testid={`member-role-pending-chip-${member.id}`}
                                title={t('ownerConsole.members.unsavedChangesTitle')}
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fde68a'
                                }}
                              >
                                {t('ownerConsole.members.unsavedChangesChip')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontWeight: 700 }}>{member.role}</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
                          {(() => {
                            // Findings #121: one plain status; token counts under Detalles técnicos.
                            const memberStatus = getOwnerConsoleMemberStatus(member)
                            const palette = memberStatus.tone === 'revoked'
                              ? { backgroundColor: '#fee2e2', color: '#991b1b' }
                              : memberStatus.tone === 'warn'
                                ? { backgroundColor: '#fffbeb', color: '#92400e' }
                                : { backgroundColor: '#dcfce7', color: '#166534' }
                            return (
                              <>
                                <span data-testid={`member-status-${member.id}`} style={{ padding: '3px 8px', borderRadius: 999, fontSize: 12, fontWeight: 800, ...palette }}>
                                  {t(memberStatus.labelKey)}
                                </span>
                                {memberStatus.hintKey && (
                                  <span data-testid={`member-status-hint-${member.id}`} style={{ fontSize: 12, color: '#6b7280' }}>
                                    {t(memberStatus.hintKey)}
                                  </span>
                                )}
                              </>
                            )
                          })()}
                          <details data-testid={`member-token-summary-${member.id}`}>
                            <summary style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>{t('createMember.technicalDetails')}</summary>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              <div data-testid={`member-credential-status-${member.id}`}>{t(getOwnerConsoleCredentialStatusKey(member))}</div>
                              <div data-testid={`member-active-token-count-${member.id}`}>{t('ownerConsole.members.activeTokens')}: {member.activeTokenCount}</div>
                              <div data-testid={`member-revoked-token-count-${member.id}`}>{t('ownerConsole.members.revokedTokens')}: {member.revokedTokenCount}</div>
                            </div>
                          </details>
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', color: '#6b7280' }}>
                        {isRevokedMember && member.revokedAt
                          ? `${t('ownerConsole.members.revokedAtPrefix')} ${new Date(member.revokedAt).toLocaleString()}`
                          : member.lastUsedAt
                            ? new Date(member.lastUsedAt).toLocaleString()
                            : (
                              <span
                                data-testid={`member-last-used-${member.id}`}
                                style={{
                                  display: 'inline-flex',
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  backgroundColor: '#fffbeb',
                                  color: '#92400e'
                                }}
                              >
                                {t('ownerConsole.members.neverUsed')}
                              </span>
                            )}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                        {/* Findings #171: a revoked row is read-only — no role, link, PIN or revoke buttons. */}
                        {!isRevokedMember && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              data-testid={`member-save-${member.id}`}
                              onClick={() => void saveMemberRole(member)}
                              disabled={saveButtonDisabled}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: hasPendingRoleDraft ? '1px solid #1d4ed8' : '1px solid #d1d5db',
                                backgroundColor: hasPendingRoleDraft && !saveButtonDisabled ? '#1d4ed8' : 'white',
                                color: hasPendingRoleDraft && !saveButtonDisabled ? 'white' : '#111827',
                                fontWeight: 800,
                                cursor: saveButtonDisabled ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {t('ownerConsole.action.saveRole')}
                            </button>
                            <button
                              type="button"
                              data-testid={`member-password-reset-${member.id}`}
                              onClick={() => void issueMemberPasswordReset(member)}
                              disabled={!canIssuePasswordReset}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: '1px solid #d1d5db',
                                backgroundColor: 'white',
                                fontWeight: 800,
                                cursor: !canIssuePasswordReset ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {issuingPasswordResetMemberId === member.id
                                ? t('ownerConsole.action.issuingPasswordReset')
                                : t(getOwnerConsoleCredentialActionKey(member))}
                            </button>
                            {canManage && !isRevokedMember && (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={4}
                                  data-testid={`member-pin-input-${member.id}`}
                                  placeholder={t('ownerConsole.pin.placeholder')}
                                  value={operatorPinDrafts[member.id] ?? ''}
                                  onChange={(event) => {
                                    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 4)
                                    setOperatorPinDrafts((prev) => ({ ...prev, [member.id]: digitsOnly }))
                                  }}
                                  style={{
                                    width: 84,
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    border: '1px solid #d1d5db',
                                    fontFamily: 'monospace',
                                    letterSpacing: 2
                                  }}
                                  disabled={loadState.isLoading || settingPinMemberId === member.id}
                                />
                                <button
                                  type="button"
                                  data-testid={`member-set-pin-${member.id}`}
                                  onClick={() => void setOperatorPin(member)}
                                  disabled={loadState.isLoading || settingPinMemberId === member.id || !(operatorPinDrafts[member.id] ?? '').trim()}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    border: '1px solid #d1d5db',
                                    backgroundColor: 'white',
                                    fontWeight: 800,
                                    cursor: (loadState.isLoading || settingPinMemberId === member.id) ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {settingPinMemberId === member.id
                                    ? t('ownerConsole.action.settingPin')
                                    : t('ownerConsole.action.setPin')}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`member-clear-pin-${member.id}`}
                                  onClick={() => void clearOperatorPin(member)}
                                  disabled={loadState.isLoading || clearingPinMemberId === member.id}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    border: '1px solid #d1d5db',
                                    backgroundColor: 'white',
                                    fontWeight: 800,
                                    cursor: (loadState.isLoading || clearingPinMemberId === member.id) ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {clearingPinMemberId === member.id
                                    ? t('ownerConsole.action.clearingPin')
                                    : t('ownerConsole.action.clearPin')}
                                </button>
                              </div>
                            )}
                            <button
                              type="button"
                              data-testid={`member-revoke-${member.id}`}
                              onClick={() => void revokeMember(member.id)}
                              disabled={!editable || !canManage || loadState.isLoading}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: '1px solid #d1d5db',
                                backgroundColor: 'white',
                                fontWeight: 800,
                                cursor: (!editable || !canManage || loadState.isLoading) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {t('ownerConsole.action.revokeMember')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {issuedPasswordReset && (
          <div data-testid="member-password-reset-output" style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <div style={{ fontWeight: 800, color: '#1d4ed8' }}>
              {issuedPasswordReset.purpose === 'activation'
                ? t('ownerConsole.passwordReset.activationTitle')
                : t('ownerConsole.passwordReset.title')}
            </div>
            <div style={{ marginTop: 6 }}>{issuedPasswordReset.memberLabel}</div>
            <div style={{ marginTop: 6, color: '#475569' }}>
              {t('ownerConsole.passwordReset.expiresAt')}: {new Date(issuedPasswordReset.expiresAt).toLocaleString()}
            </div>
            <div data-testid="member-password-reset-url" style={{ marginTop: 8, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {issuedPasswordReset.resetUrl}
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(issuedPasswordReset.resetUrl)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #1d4ed8', backgroundColor: 'white', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('ownerConsole.passwordReset.copy')}
              </button>
              <button
                type="button"
                onClick={() => setIssuedPasswordReset(null)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('ownerConsole.passwordReset.dismiss')}
              </button>
            </div>
          </div>
        )}
        {pinStatusMessage && (
          <div
            data-testid="member-pin-status"
            style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontWeight: 700 }}
          >
            {pinStatusMessage}
          </div>
        )}
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 10 }}>
          {t('ownerConsole.members.note')}
        </div>
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>
          {t('ownerConsole.members.migrationNote')}
        </div>
      </div>
      )}

      {activeSection === 'tokens' && (
      <>
      <div style={{ padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>{t('ownerConsole.create.title')}</h3>
          <button
            type="button"
            onClick={() => navigate('/create-account')}
            disabled={!canManage || loadState.isLoading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              fontWeight: 800,
              cursor: (!canManage || loadState.isLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {t('ownerConsole.create.openOnboarding')}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12, alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('ownerConsole.create.label')}</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('ownerConsole.create.labelPlaceholder')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              disabled={!canManage || loadState.isLoading}
            />
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('ownerConsole.create.role')}</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as any)}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              disabled={!canManage || loadState.isLoading}
            >
              <option value="viewer">{t('ownerConsole.create.roleOption.viewer')}</option>
              <option value="contributor">{t('ownerConsole.create.roleOption.contributor')}</option>
              <option value="editor">{t('ownerConsole.create.roleOption.editor')}</option>
            </select>
          </label>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => void createUserToken()}
            disabled={!canManage || loadState.isLoading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#111827',
              color: 'white',
              fontWeight: 800,
              cursor: (!canManage || loadState.isLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {t('ownerConsole.create.createToken')}
          </button>

          <button
            type="button"
            onClick={() => void reload()}
            disabled={loadState.isLoading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              fontWeight: 800,
              cursor: loadState.isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {t('ownerConsole.create.refresh')}
          </button>
        </div>

        {createdToken && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#ecfdf5', border: '1px solid #bbf7d0' }}>
            <div style={{ fontWeight: 800, color: '#065f46' }}>{t('ownerConsole.createdToken.title')}</div>
            <div style={{ fontFamily: 'monospace', marginTop: 6, wordBreak: 'break-all' }}>{createdToken}</div>
            <div style={{ marginTop: 10, display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(createdToken)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #10b981', backgroundColor: 'white', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('ownerConsole.createdToken.copy')}
              </button>
              <button
                type="button"
                onClick={() => setCreatedToken('')}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('ownerConsole.createdToken.dismiss')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 18, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <h3 style={{ marginTop: 0 }}>{t('ownerConsole.existing.title')}</h3>
        {loadState.isLoading ? (
          <div style={{ color: '#6b7280' }}>{t('ownerConsole.existing.loading')}</div>
        ) : tokens.length === 0 ? (
          <div style={{ color: '#6b7280' }}>{t('ownerConsole.existing.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.member')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.membership')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.user')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.role')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.actor')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.compatibilityMember')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.lastUsed')}</th>
                  <th style={{ padding: '10px 8px' }}>{t('ownerConsole.table.status')}</th>
                  <th style={{ padding: '10px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => {
                  const revoked = Boolean(token.revokedAt)
                  const isCurrentToken = Boolean(me?.currentTokenId) && me?.currentTokenId === token.id
                  return (
                    <tr key={token.id} data-testid={`token-row-${token.id}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 8px' }}>{token.label}</td>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 12 }}>{token.tenantMembershipId ?? '—'}</td>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 12 }}>{token.userId ?? '—'}</td>
                      <td style={{ padding: '10px 8px' }}>{token.role}</td>
                      <td style={{ padding: '10px 8px' }}>{token.actorId}</td>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 12 }}>{token.id}</td>
                      <td style={{ padding: '10px 8px', color: '#6b7280' }}>{token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : '—'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 800,
                          backgroundColor: revoked ? '#fee2e2' : '#dcfce7',
                          color: revoked ? '#991b1b' : '#166534'
                        }}>
                          {revoked ? t('ownerConsole.status.revoked') : t('ownerConsole.status.active')}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                        <button
                          type="button"
                          data-testid={`token-revoke-${token.id}`}
                          onClick={() => void revoke(token.id)}
                          disabled={!canManage || revoked || isCurrentToken}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            backgroundColor: 'white',
                            fontWeight: 800,
                            cursor: (!canManage || revoked || isCurrentToken) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {t('ownerConsole.action.revoke')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
      </SectionNav>
      </div>

      <Drawer
        open={drawerSection === 'organizations'}
        onClose={() => setDrawerSection(null)}
        title={t('ownerConsole.nav.organizations')}
      >
      <div>
        <h3 style={{ marginTop: 0 }}>{t('ownerConsole.partnerOrganizations.title')}</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('ownerConsole.partnerOrganizations.subtitle')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end', marginTop: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('ownerConsole.partnerOrganizations.nameLabel')}</span>
            <input
              data-testid="partner-organization-name-input"
              value={partnerOrganizationName}
              onChange={(event) => setPartnerOrganizationName(event.target.value)}
              placeholder={t('ownerConsole.partnerOrganizations.namePlaceholder')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              disabled={!canManage || loadState.isLoading}
            />
          </label>
          <button
            type="button"
            data-testid="partner-organization-create-button"
            onClick={() => void createPartnerOrganization()}
            disabled={!canManage || loadState.isLoading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#111827',
              color: 'white',
              fontWeight: 800,
              cursor: (!canManage || loadState.isLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {t('ownerConsole.partnerOrganizations.create')}
          </button>
        </div>
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 10 }}>
          {t('ownerConsole.partnerOrganizations.note')}
        </div>
        {loadState.isLoading ? (
          <div style={{ color: '#6b7280', marginTop: 12 }}>{t('ownerConsole.partnerOrganizations.loading')}</div>
        ) : partnerOrganizations.length === 0 ? (
          <div style={{ color: '#6b7280', marginTop: 12 }}>{t('ownerConsole.partnerOrganizations.empty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            {partnerOrganizations.map((organization) => (
              <div
                key={organization.id}
                data-testid={`partner-organization-row-${organization.id}`}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', backgroundColor: '#fcfcfd' }}
              >
                <div style={{ fontWeight: 700 }}>{organization.displayName}</div>
                <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: organization.status === 'active' ? '#dcfce7' : '#f3f4f6', color: organization.status === 'active' ? '#166534' : '#4b5563' }}>
                  {t(`ownerConsole.partnerOrganizations.status.${organization.status}` as const)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      </Drawer>

      <Drawer
        open={drawerSection === 'groups'}
        onClose={() => setDrawerSection(null)}
        title={t('ownerConsole.nav.groups')}
      >
      <div>
        <h3 style={{ marginTop: 0 }}>{t('ownerConsole.groups.title')}</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('ownerConsole.groups.subtitle')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end', marginTop: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('ownerConsole.groups.nameLabel')}</span>
            <input
              data-testid="group-name-input"
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder={t('ownerConsole.groups.namePlaceholder')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              disabled={!canManage || loadState.isLoading}
            />
          </label>
          <button
            type="button"
            data-testid="group-create-button"
            onClick={() => void createGroup()}
            disabled={!canManage || loadState.isLoading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#111827',
              color: 'white',
              fontWeight: 800,
              cursor: (!canManage || loadState.isLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {t('ownerConsole.groups.create')}
          </button>
        </div>
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 10 }}>
          {t('ownerConsole.groups.note')}
        </div>

        {loadState.isLoading ? (
          <div style={{ color: '#6b7280', marginTop: 12 }}>{t('ownerConsole.groups.loading')}</div>
        ) : groups.length === 0 ? (
          <div style={{ color: '#6b7280', marginTop: 12 }}>{t('ownerConsole.groups.empty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            {groups.map((group) => {
              const availableMembers = activeMembers.filter((member) => !group.memberships.some((membership) => membership.tenantMembershipId === member.id))
              return (
                <div
                  key={group.id}
                  data-testid={`group-row-${group.id}`}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, backgroundColor: '#fcfcfd' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 18 }}>{group.displayName}</div>
                      <div style={{ color: '#6b7280', marginTop: 4 }}>
                        {t('ownerConsole.groups.createdBy')}: {group.createdByDisplayName}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 800, backgroundColor: group.status === 'active' ? '#dcfce7' : '#f3f4f6', color: group.status === 'active' ? '#166534' : '#4b5563' }}>
                        {t(`ownerConsole.groups.status.${group.status}` as const)}
                      </span>
                      <span data-testid={`group-active-count-${group.id}`} style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#eff6ff', color: '#1d4ed8' }}>
                        {t('ownerConsole.groups.activeMembers')}: {group.activeMembershipCount}
                      </span>
                      <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#fef3c7', color: '#92400e' }}>
                        {t('ownerConsole.groups.invitedMembers')}: {group.invitedMembershipCount}
                      </span>
                      <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b' }}>
                        {t('ownerConsole.groups.revokedMembers')}: {group.revokedMembershipCount}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                    {group.memberships.length === 0 ? (
                      <div style={{ color: '#6b7280' }}>{t('ownerConsole.groups.noMemberships')}</div>
                    ) : group.memberships.map((membership) => (
                      <div
                        key={membership.id}
                        data-testid={`group-membership-row-${group.id}-${membership.id}`}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '10px 12px', borderRadius: 10, backgroundColor: 'white', border: '1px solid #e5e7eb', flexWrap: 'wrap' }}
                      >
                        <div style={{ display: 'grid', gap: 4 }}>
                          <div style={{ fontWeight: 700 }}>{membership.displayName}</div>
                          <div style={{ color: '#6b7280', fontSize: 12 }}>{membership.label} · {membership.actorId}</div>
                          {membership.partnerOrganizationName && (
                            <div data-testid={`group-membership-partner-organization-${group.id}-${membership.id}`} style={{ color: '#6b7280', fontSize: 12 }}>
                              {t('ownerConsole.groups.partnerOrganizationAssigned')}: {membership.partnerOrganizationName}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#eef2ff', color: '#3730a3' }}>
                            {t(`ownerConsole.groups.role.${membership.role}` as const)}
                          </span>
                          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: membership.status === 'active' ? '#dcfce7' : membership.status === 'invited' ? '#fef3c7' : '#fee2e2', color: membership.status === 'active' ? '#166534' : membership.status === 'invited' ? '#92400e' : '#991b1b' }}>
                            {t(`ownerConsole.groups.membershipStatus.${membership.status}` as const)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 180px 220px auto', gap: 12, alignItems: 'end' }}>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{t('ownerConsole.groups.addMemberLabel')}</span>
                      <select
                        data-testid={`group-member-select-${group.id}`}
                        value={groupMemberDrafts[group.id] ?? ''}
                        onChange={(event) => setGroupMemberDrafts((prev) => ({
                          ...prev,
                          [group.id]: event.target.value
                        }))}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                        disabled={!canManage || loadState.isLoading || availableMembers.length === 0}
                      >
                        <option value="">{t('ownerConsole.groups.selectMemberPlaceholder')}</option>
                        {availableMembers.map((member) => (
                          <option key={member.id} value={member.id}>{member.label}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{t('ownerConsole.groups.addMemberPartnerOrganizationLabel')}</span>
                      <select
                        data-testid={`group-member-partner-organization-${group.id}`}
                        value={groupPartnerOrganizationDrafts[group.id] ?? ''}
                        onChange={(event) => setGroupPartnerOrganizationDrafts((prev) => ({
                          ...prev,
                          [group.id]: event.target.value
                        }))}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                        disabled={!canManage || loadState.isLoading || availableMembers.length === 0}
                      >
                        <option value="">{t('ownerConsole.groups.selectPartnerOrganizationPlaceholder')}</option>
                        {partnerOrganizations.map((organization) => (
                          <option key={organization.id} value={organization.id}>{organization.displayName}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{t('ownerConsole.groups.addMemberRoleLabel')}</span>
                      <select
                        data-testid={`group-member-role-${group.id}`}
                        value={groupRoleDrafts[group.id] ?? 'viewer'}
                        onChange={(event) => setGroupRoleDrafts((prev) => ({
                          ...prev,
                          [group.id]: event.target.value as 'owner' | 'editor' | 'viewer'
                        }))}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                        disabled={!canManage || loadState.isLoading || availableMembers.length === 0}
                      >
                        <option value="owner">{t('ownerConsole.groups.role.owner')}</option>
                        <option value="editor">{t('ownerConsole.groups.role.editor')}</option>
                        <option value="viewer">{t('ownerConsole.groups.role.viewer')}</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      data-testid={`group-member-add-${group.id}`}
                      onClick={() => void addGroupMember(group)}
                      disabled={!canManage || loadState.isLoading || availableMembers.length === 0}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        fontWeight: 800,
                        cursor: (!canManage || loadState.isLoading || availableMembers.length === 0) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {t('ownerConsole.groups.addMember')}
                    </button>
                  </div>
                  {availableMembers.length === 0 && (
                    <div style={{ color: '#6b7280', fontSize: 12, marginTop: 10 }}>
                      {t('ownerConsole.groups.noAvailableMembers')}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      </Drawer>
    </div>
  )
}
